import dayjs from 'dayjs';

// The biggest revenue day of the month so far - which day, how much, what share of the month it was, how it compares
// with an ordinary day, and which department carried it. Pure (no clock, no store) so it can be tested.
//
//   rows         the day rows from the API: { date: 'YYYY-MM-DD', dayLabel, total, byDept: { [deptId]: amount } }
//   elapsedDays  how many days of the month have happened - a future, zero-filled day is never "the biggest"
//   departments  meta.departments: { deptId, deptName }, to name the department that led the day
//
// Returns null when there is no revenue at all (nothing to call biggest). When two days tie, the earlier one wins.
export const biggestRevenueDay = (rows, { elapsedDays, departments = [] }) => {
  const active = (rows || []).filter((r) => dayjs(r.date).date() <= elapsedDays);
  if (!active.length) return null;

  const best = active.reduce((top, r) => (Number(r.total) > (top ? Number(top.total) : 0) ? r : top), null);
  if (!best || !(Number(best.total) > 0)) return null;

  const monthSoFar = active.reduce((sum, r) => sum + (Number(r.total) || 0), 0);
  const dailyAverage = elapsedDays > 0 ? monthSoFar / elapsedDays : 0;

  // the department that carried that day (largest amount in its byDept), if any department is named
  let lead = null;
  Object.entries(best.byDept || {}).forEach(([deptId, amount]) => {
    const value = Number(amount) || 0;
    if (value > 0 && (!lead || value > lead.amount)) lead = { deptId: Number(deptId), amount: value };
  });
  if (lead) lead.deptName = departments.find((d) => Number(d.deptId) === lead.deptId)?.deptName || null;

  return {
    date: best.date,
    dayLabel: best.dayLabel,
    total: Number(best.total),
    shareOfMonth: monthSoFar > 0 ? Number(((Number(best.total) / monthSoFar) * 100).toFixed(1)) : null,
    multipleOfAverage: dailyAverage > 0 ? Number((Number(best.total) / dailyAverage).toFixed(1)) : null,
    lead,
  };
};

// Each department's own highest income day of the month so far: the day, the amount, and what share of that
// department's month it was. A department with no revenue has date null. Same rules as biggestRevenueDay: only days that
// have happened count, and a tie goes to the earlier day. Returned in the order of `departments`.
export const highestDayByDepartment = (rows, { elapsedDays, departments = [] }) => {
  const active = (rows || []).filter((r) => dayjs(r.date).date() <= elapsedDays);
  return departments.map((dept) => {
    let best = null;
    let monthTotal = 0;
    active.forEach((r) => {
      const amount = Number(r.byDept?.[dept.deptId]) || 0;
      monthTotal += amount;
      if (amount > (best ? best.amount : 0)) best = { date: r.date, dayLabel: r.dayLabel, amount };
    });
    return {
      deptId: dept.deptId,
      deptName: dept.deptName,
      date: best ? best.date : null,
      dayLabel: best ? best.dayLabel : null,
      amount: best ? best.amount : 0,
      shareOfDeptMonth: best && monthTotal > 0 ? Number(((best.amount / monthTotal) * 100).toFixed(1)) : null,
    };
  });
};
